import {stringify} from "comment-json";
import {omit, startCase} from "lodash";
import {DEFAULT_CARD_HEIGHT, DEFAULT_CARD_WIDTH} from "../../../../components/CodyEngineWizard/WizardModal";
import {GraphPoint, makeNodeFromJs, Node, NodeId, NodeType} from "../../../../model/Graph";
import {CodyResponse, CodyResponseType} from "../../../Cody";
import {UiSchema} from "../../../cody-wizard/ui/ui-schema";
import {
  convertJsonSchemaToShorthandIfPossible
} from "../../../json/schema/convert-json-schema-to-shorthand-if-possible";
import {Action, CommandAction, isCommandAction} from "../../action-definitions";
import {nodeLabel, removeDefaultService} from "../../default-service";
import {getNodeIdFromProophBoardDescription, ProophBoardDescriptionKeys} from "../../descriptions";
import {isRecordEvent, ThenType, visitRulesThen} from "../../rule-definitions";
import {ApplyTask, makeApplyTask, runApplyTasks} from "../../run-apply-tasks";
import {PlayCommandRuntimeInfo} from "../commands/command-definitions";
import {PlayEventRuntimeInfo} from "../events/event-definitions";
import {
  CONNECT_SPACING,
  geoBelow,
  geoBelowRight,
  geoRight,
  getNextGeo, SLICE_HEIGHT,
  SPACING,
  X_SLICE_SPACING, Y_CTX_SPACING,
  Y_SLICE_SPACING
} from "../graph-manipulation";
import {getViewRuntimeInformation} from "../information/get-view-runtime-information";
import {
  PlayInformationRegistry,
  PlayInformationRuntimeInfo,
} from "../information/information-definitions";
import {prepareDiffInformation} from "../information/prepare-diff-information";
import {PlayshotApplyContext} from "../PlayshotChanged";
import {PageDefinition} from "./page-definitions";

interface CommandSlice {
  slice: Node;
  page: Node;
  command: Node;
  // @TODO: Handle Command Dependencies
  // @TODO: Handle Aggregate
  events: Node[];
}

interface ViewSlice {
  slice: Node;
  page: Node;
  views: Node[];
  nextPage?: Node;
}

interface EventContext {
  info: PlayEventRuntimeInfo;
  node: Node;
}


export const applySlices = async (pageId: NodeId, page: PageDefinition, ctx: PlayshotApplyContext): Promise<AsyncIterable<CodyResponse>> => {
  const tasks: ApplyTask[] = [];

  const addedViews: PlayInformationRegistry = {};

  const commands = getAllPageCommands(page, ctx);

  commands.forEach(command => {
    tasks.push(...makeAddCommandSliceTasks(command, page, ctx));
  })


  if(tasks.length === 0) {
    tasks.push(makeAddPageTask(pageId, page, ctx));
  }

  return runApplyTasks(tasks);
}

const getAllPageCommands = (page: PageDefinition, ctx: PlayshotApplyContext): PlayCommandRuntimeInfo[] => {
  const commands: PlayCommandRuntimeInfo[] = [];

  commands.push(...page.commands.filter(c => typeof c === "string" || c.type === "command").map(c => {
    const commandName = typeof c === "string" ? c : (c as CommandAction).command;
    return ctx.playshot.playConfig.commands[commandName]
  }).filter(c => c))

  page.components.forEach(view => {
    if(typeof view === "string") {
      const viewInformation = getViewRuntimeInformation(view, ctx.playshot);

      if(viewInformation) {
        commands.push(...getCommandsFromUiSchema(viewInformation.uiSchema || {}, ctx))
      }

      return;
    } else {
      if(view.uiSchema) {
        commands.push(...getCommandsFromUiSchema(view.uiSchema, ctx));
      }

      const viewInformation = getViewRuntimeInformation(view.view, ctx.playshot);

      if(viewInformation) {
        commands.push(...getCommandsFromUiSchema(viewInformation.uiSchema || {}, ctx));
      }
    }
  })

  return commands;
}

const getCommandsFromUiSchema = (uiSchema: UiSchema, ctx: PlayshotApplyContext): PlayCommandRuntimeInfo[] => {
  const commands: PlayCommandRuntimeInfo[] = [];

  if(uiSchema['ui:options'] && (uiSchema['ui:options'] as any).actions) {
    const actions: Action[] = (uiSchema['ui:options'] as any).actions;

    actions.forEach(a => {
      if(isCommandAction(a) && ctx.playshot.playConfig.commands[a.command]) {
        commands.push(ctx.playshot.playConfig.commands[a.command]);
      }
    })
  }

  return commands;
}

const getEventsRecordedByCommand = (command: PlayCommandRuntimeInfo, ctx: PlayshotApplyContext): PlayEventRuntimeInfo[] => {
  const rules = ctx.playshot.playConfig.commandHandlers[command.desc.name] || [];

  const events: PlayEventRuntimeInfo[] = [];

  visitRulesThen(rules, (then: ThenType): ThenType => {
    if(isRecordEvent(then)) {
      const event = ctx.playshot.playConfig.events[then.record.event];

      if(event) {
        events.push(event);
      }
    }


    return then;
  })

  return events;
}

const makeAddCommandSliceTasks = (command: PlayCommandRuntimeInfo, page: PageDefinition, ctx: PlayshotApplyContext): ApplyTask[] => {
  const tasks: ApplyTask[] = [];

  const commandId = getNodeIdFromProophBoardDescription(command.desc);

  if(ctx.graph.getNode(commandId)) {
    return tasks;
  }

  const commandSlice: Partial<CommandSlice> = {events: []};

  tasks.push(makeAddCommandSliceFrameTask(command, commandSlice, ctx));
  tasks.push(makeAddCommandSlicePageTask(page, commandSlice, ctx));
  tasks.push(makeCommandSliceCommandTask(command, commandSlice, ctx));

  const events = getEventsRecordedByCommand(command, ctx);

  events.forEach(event => {
    const eventContext: Partial<EventContext> = {info: event};
    tasks.push(makeCommandSliceEventTask(eventContext as EventContext, commandSlice as CommandSlice, ctx));

    const policies = ctx.playshot.playConfig.eventPolicies[event.desc.name] || {};

    Object.values(policies).forEach(policy => {
      if(policy.projection) {
        const information = Object.values(ctx.playshot.playConfig.types)
          .filter(iri => iri.desc.projection === policy.projection)
          .shift();

        if(information) {
          tasks.push(...makeInformationProjectionSliceTasks(eventContext as EventContext, information, ctx));
        }
      } else {
        // @TODO apply processor slice
      }
    })
  })

  return tasks;
}

const makeInformationProjectionSliceTasks = (event: EventContext, information: PlayInformationRuntimeInfo, ctx: PlayshotApplyContext): ApplyTask[] => {
  const tasks: ApplyTask[] = [];
  const {views} = ctx.playshot.playConfig;
  let viewName: string | undefined;

  for (const viewKey in views) {
    if(views[viewKey].information === information.desc.name) {
      viewName = viewKey;
    }
  }

  Object.values(ctx.playshot.playConfig.pages)
    .filter(p => {
      for (const component of p.components) {
        const componentName = typeof component === "string" ? component : component.view;

        return componentName === viewName;
      }
    })
    .forEach(page => {
      const viewSlice: Partial<ViewSlice> = {views: []};

      tasks.push(makeViewInformationSliceFrameTask(information, page, viewSlice, ctx));
      tasks.push(makeViewSliceInformationTask(information, event, viewSlice, ctx));
      tasks.push(makeViewSlicePageTask(page, viewSlice, ctx));
    })

  return tasks;
}

const makeViewInformationSliceFrameTask = (information: PlayInformationRuntimeInfo, page: PageDefinition, viewSlice: Partial<ViewSlice>, ctx: PlayshotApplyContext): ApplyTask => {
  return makeApplyTask("ViewInformationSliceFrameTask", async () => {
    viewSlice.slice = makeNodeFromJs({
      id: ctx.graph.createCellId(),
      type: NodeType.feature,
      name: `View ${nodeLabel(information.desc.name)}`,
      size: {
        width: 520,
        height: SLICE_HEIGHT
      }
    })

    ctx.graph.addNode(viewSlice.slice);
    ctx.graph.switchParent(viewSlice.slice, ctx.syncContext, getNextGeo(ctx.addedNodes), true);
    ctx.graph.focusNode(viewSlice.slice);

    ctx.addedNodes[viewSlice.slice.getId()] = ctx.graph.getNode(viewSlice.slice.getId()) || viewSlice.slice;

    return {
      cody: `I've added a view slice that documents what information is shown on page "${nodeLabel(page.name)}".`,
      type: CodyResponseType.Info
    }
  })
}

const makeViewSliceInformationTask = (information: PlayInformationRuntimeInfo, event: EventContext, viewSlice: Partial<ViewSlice>, ctx: PlayshotApplyContext): ApplyTask => {
  return makeApplyTask("ViewSliceInformationTask", async () => {

    const nodeId = getNodeIdFromProophBoardDescription(information.desc);

    // Add a copy if information already exists on the board
    const infoId = ctx.graph.getNode(nodeId) ? ctx.graph.createCellId() : nodeId;

    const preparedInfo = prepareDiffInformation(information, ctx.playshot);

    const node = makeNodeFromJs({
      id: infoId,
      type: NodeType.document,
      name: nodeLabel(information.desc.name),
      metadata: stringify(omit({
        ...preparedInfo.desc,
        schema: preparedInfo.schema,
        uiSchema: preparedInfo.uiSchema,
        querySchema: preparedInfo.querySchema,
        resolve: preparedInfo.resolve,
        projection: preparedInfo.projection,
        initialize: preparedInfo.initialize,
      }, [
        "name",
      ]))
    })

    const edgePoint = {
      x: event.node.getParent()!.getGeometry().x + event.node.getGeometry().x + ((X_SLICE_SPACING * 2 + SPACING) / 2),
      y: Y_CTX_SPACING + event.node.getGeometry().y - (CONNECT_SPACING / 2)
    };

    ctx.graph.addNode(node);
    ctx.graph.switchParent(node, viewSlice.slice!, {
      x: X_SLICE_SPACING,
      y: Y_SLICE_SPACING + DEFAULT_CARD_HEIGHT + (CONNECT_SPACING - (Y_SLICE_SPACING - SPACING))
    }, true);
    ctx.graph.focusNode(node);
    ctx.graph.connectNodes(event.node, node, undefined, [edgePoint], true);

    addNodeBeforeSliceNode(node, viewSlice.slice!, ctx);

    viewSlice.views!.push(ctx.graph.getNode(infoId) || node);

    return {
      cody: `I've added an information card for "${nodeLabel(information.desc.name)}" to the board`
    };
  })
}

const makeViewSlicePageTask = (page: PageDefinition, viewSlice: Partial<ViewSlice>, ctx: PlayshotApplyContext): ApplyTask => {
  return makeApplyTask("ViewSlicePageTask", async () => {
    const pageNode = makeNodeFromJs({
      id: ctx.graph.createCellId(),
      type: NodeType.ui,
      name: nodeLabel(page.name),
      metadata: stringify(omit({...page, views: page.components}, [
        "name",
        "topLevel",
        "components",
        ...ProophBoardDescriptionKeys
      ]))
    })

    const lastView = viewSlice.views![viewSlice.views!.length -1];

    ctx.graph.addNode(pageNode);
    ctx.graph.switchParent(pageNode, viewSlice.slice!, {x: lastView.getGeometry().x + DEFAULT_CARD_WIDTH, y: Y_SLICE_SPACING}, true);
    ctx.graph.focusNode(pageNode);
    viewSlice.page = ctx.graph.getNode(pageNode.getId()) || pageNode;

    viewSlice.views!.forEach(view => {
      ctx.graph.connectNodes(view, pageNode, undefined, [{
        x: viewSlice.page!.getGeometry().x - (DEFAULT_CARD_WIDTH / 2),
        y: viewSlice.page!.getGeometry().y + viewSlice.page!.getSize().height + (CONNECT_SPACING / 2)
      }], true);
    })

    addNodeBeforeSliceNode(viewSlice.page, viewSlice.slice!, ctx);

    return {
      cody: `I've added a UI card for the new page "${nodeLabel(page.name)}" to the slice "${viewSlice.slice!.getName()}"`,
      type: CodyResponseType.Info
    };
  })
}

const makeAddCommandSliceFrameTask = (command: PlayCommandRuntimeInfo, commandSlice: Partial<CommandSlice>, ctx: PlayshotApplyContext): ApplyTask => {
  return makeApplyTask("AddCommandSliceFrameTask", async () => {
    commandSlice.slice = makeNodeFromJs({
      id: ctx.graph.createCellId(),
      type: NodeType.feature,
      name: nodeLabel(command.desc.name),
      size: {
        width: 520,
        height: SLICE_HEIGHT
      }
    })

    ctx.graph.addNode(commandSlice.slice);
    ctx.graph.switchParent(commandSlice.slice, ctx.syncContext, getNextGeo(ctx.addedNodes), true);
    ctx.graph.focusNode(commandSlice.slice);

    ctx.addedNodes[commandSlice.slice.getId()] = ctx.graph.getNode(commandSlice.slice.getId()) || commandSlice.slice;

    return {
      cody: `I've added a command slice that documents how the command "${nodeLabel(command.desc.name)}" is processed.`,
      type: CodyResponseType.Info
    }
  })
}

const makeAddCommandSlicePageTask = (page: PageDefinition, commandSlice: Partial<CommandSlice>, ctx: PlayshotApplyContext): ApplyTask => {
  return makeApplyTask("AddCommandSlicePageTask", async () => {
    const pageNode = makeNodeFromJs({
      id: ctx.graph.createCellId(),
      type: NodeType.ui,
      name: nodeLabel(page.name),
      metadata: stringify(omit({...page, views: page.components}, [
        "name",
        "topLevel",
        "components",
        ...ProophBoardDescriptionKeys
      ]))
    })

    ctx.graph.addNode(pageNode);
    ctx.graph.switchParent(pageNode, commandSlice.slice!, {x: X_SLICE_SPACING, y: Y_SLICE_SPACING}, true);
    ctx.graph.focusNode(pageNode);
    commandSlice.page = ctx.graph.getNode(pageNode.getId()) || pageNode;

    addNodeBeforeSliceNode(commandSlice.page, commandSlice.slice!, ctx);

    return {
      cody: `I've added a UI card for the new page "${nodeLabel(page.name)}" to the command slice "${commandSlice.slice!.getName()}"`,
      type: CodyResponseType.Info
    };
  })
}

const makeCommandSliceCommandTask = (command: PlayCommandRuntimeInfo, commandSlice: Partial<CommandSlice>, ctx: PlayshotApplyContext): ApplyTask => {
  return makeApplyTask("CommandSliceCommandTask", async () => {
    const rules = ctx.playshot.playConfig.commandHandlers[command.desc.name] || [];

    const commandNode = makeNodeFromJs({
      id: getNodeIdFromProophBoardDescription(command.desc),
      type: NodeType.command,
      name: nodeLabel(command.desc.name),
      metadata: stringify(omit({
        ...command.desc,
        schema: convertJsonSchemaToShorthandIfPossible(command.schema),
        uiSchema: command.uiSchema,
        rules,
        // @TODO: handle factory
      }, [
        "name",
        ...ProophBoardDescriptionKeys
      ]))
    })

    ctx.graph.addNode(commandNode);
    ctx.graph.switchParent(
      commandNode,
      commandSlice.slice!,
      // Remove the page Y spacing diff from connect spacing, so that command is centered in API lane
      geoBelow(commandSlice.page!, CONNECT_SPACING - (Y_SLICE_SPACING - SPACING)),
      true
    );
    ctx.graph.focusNode(commandNode);

    commandSlice.command = ctx.graph.getNode(commandNode.getId()) || commandNode;

    ctx.graph.connectNodes(commandSlice.page!, commandSlice.command);

    addNodeBeforeSliceNode(commandSlice.command, commandSlice.slice!, ctx);

    return {
      cody: `I've added a command card for "${nodeLabel(command.desc.name)}" to the board`,
      type: CodyResponseType.Info
    }
  })
}

const makeCommandSliceEventTask = (event: EventContext, commandSlice: CommandSlice, ctx: PlayshotApplyContext): ApplyTask => {
  const nodeId = getNodeIdFromProophBoardDescription(event.info.desc);

  // Insert a copy if event already exists
  const eventId = ctx.graph.getNode(nodeId) ? ctx.graph.createCellId() : nodeId;

  return makeApplyTask("CommandSliceEventTask", async () => {
    const node = makeNodeFromJs({
      id: eventId,
      type: NodeType.event,
      name: nodeLabel(event.info.desc.name),
      metadata: stringify(omit({
        ...event.info.desc,
        schema: convertJsonSchemaToShorthandIfPossible(event.info.schema),
        // @TODO: handle factory
      }, [
        "name",
        ...ProophBoardDescriptionKeys
      ]))
    })

    const geo: GraphPoint = commandSlice.events.length > 0
      ? geoRight(commandSlice.events[commandSlice.events.length - 1], 50)
      : geoBelowRight(commandSlice.command, 0);

    ctx.graph.addNode(node);
    ctx.graph.switchParent(node, commandSlice.slice, geo, true);
    ctx.graph.focusNode(node);
    ctx.graph.connectNodes(commandSlice.command, node, undefined, [{
      x: geo.x - (DEFAULT_CARD_WIDTH / 2),
      y: geo.y - (CONNECT_SPACING / 2)
    }], true);

    const mxGraphNode = ctx.graph.getNode(node.getId()) || node;

    commandSlice.events.push(mxGraphNode);

    addNodeBeforeSliceNode(mxGraphNode, commandSlice.slice, ctx);

    event.node = mxGraphNode;

    return {
      cody: `I've added the event "${nodeLabel(event.info.desc.name)}". It records the result of processing command "${commandSlice.command.getName()}".`,
      type: CodyResponseType.Info
    }
  })
}

// Slice node needs to be the last added node so that follow up element position is calculated based on slice geo
const addNodeBeforeSliceNode = (node: Node, slice: Node, ctx: PlayshotApplyContext) => {
  const sliceEle = ctx.addedNodes[slice.getId()];
  delete ctx.addedNodes[sliceEle.getId()];
  ctx.addedNodes[node.getId()] = node;
  ctx.addedNodes[sliceEle.getId()] = sliceEle;
}

const makeAddPageTask = (pageId: NodeId, page: PageDefinition, ctx: PlayshotApplyContext): ApplyTask => {
  return makeApplyTask("AddPageTask", async () => {
    const existingNode = ctx.graph.getNode(pageId);

    if(existingNode) {
      return {
        cody: `Hhm, I'm skipping the change "New Page: ${nodeLabel(page.name)}", because the UI card seems to exist already on the board.`,
        details: `There is at least an element on the board with ID: ${pageId}`,
        type: CodyResponseType.Warning
      }
    }

    const node = makeNodeFromJs({
      id: pageId,
      type: NodeType.ui,
      name: nodeLabel(page.name),
      metadata: stringify(omit({...page, views: page.components}, [
        "name",
        "topLevel",
        "components",
        ...ProophBoardDescriptionKeys
      ]))
    })

    ctx.graph.addNode(node);
    ctx.graph.switchParent(node, ctx.syncContext, getNextGeo(ctx.addedNodes), true);
    ctx.graph.focusNode(node);

    ctx.addedNodes[node.getId()] = ctx.graph.getNode(node.getId()) || node;



    return {
      cody: `I've added a UI card for the new page "${nodeLabel(page.name)}" to the board`,
      type: CodyResponseType.Info
    };
  })
}
